import { createContext, useContext, useState, useEffect, useRef } from "react";
import authService from "../services/authService";
import { Roles } from "../constants/permissions";
import b2bPreOrdersService from "../services/b2bPreOrdersService";

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [globalSettings, setGlobalSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const loggingOut = useRef(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const [accessToken, setAccessToken] = useState(null);

  const checkAuth = async () => {
    if (loggingOut.current) return;
    try {
      const response = await authService.fetchProfile();
      const userData = response?.user || response;

      if (userData && (userData.role || userData.uid || userData.id)) {
        const role = userData.role;
        if (!Object.values(Roles).includes(role) || !userData.companyId) {
          console.warn("Unauthorized role or missing companyId", {
            role,
            companyId: userData.companyId,
          });
          throw new Error("Unauthorized: Not a valid B2B Client account.");
        }

        setUser(userData);
        setAccessToken(response?.accessToken || null);
        try {
          const settings = await b2bPreOrdersService.getGlobalSettings();
          setGlobalSettings(settings);
        } catch (e) {
          console.error("Failed to fetch global settings", e);
        }
      } else {
        setUser(null);
        setAccessToken(null);
        setGlobalSettings(null);
      }
    } catch (err) {
      console.error("Auth check failed:", err.message);
      setUser(null);
      setAccessToken(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (credentials) => {
    try {
      const loginData = await authService.login(credentials);
      const userData = loginData?.user || loginData;

      if (userData && (userData.role || userData.uid || userData.id)) {
        const role = userData.role;
        if (!Object.values(Roles).includes(role) || !userData.companyId) {
          await authService.logout();
          throw new Error("Unauthorized: Not a valid B2B Client account.");
        }

        setUser(userData);
        setAccessToken(loginData?.accessToken || null);
        try {
          const settings = await b2bPreOrdersService.getGlobalSettings();
          setGlobalSettings(settings);
        } catch (e) {
          console.error("Failed to fetch global settings", e);
        }
        return loginData;
      }
      throw new Error("Login failed: Profile incomplete");
    } catch (err) {
      throw err;
    }
  };

  const logout = async () => {
    loggingOut.current = true;
    setUser(null);
    setAccessToken(null);
    setGlobalSettings(null);
    try {
      await authService.logout();
    } catch {}
  };

  const value = {
    user,
    accessToken,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    companyId: user?.companyId || null,
    company: user?.company || null,
    globalSettings,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
